import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { RecaptchaComponent } from '../../recaptcha/recaptcha.component';
import { LoginAttemptService } from '../../services/login-attempt.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    RecaptchaComponent,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm!: FormGroup;
  isSubmitting = false;
  errorMessage = '';
  showPassword = false;
  recaptchaToken = '';
  recaptchaErrorMessage = '';
  showCaptcha = false;
  remainingAttempts = 0;
  googleOAuthUrl = `${environment.websocketUrl}/auth/google`;
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private storageListener: ((event: StorageEvent) => void) | null = null;
  private broadcastChannel: BroadcastChannel | null = null;
  private popupCheckInterval: any = null;
  private localStorageCheckInterval: any = null;

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private loginAttemptService: LoginAttemptService
  ) {}

  ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });

    this.updateCaptchaVisibility();

    setTimeout(() => {
      document.querySelector('.login-form')?.classList.add('visible');
    }, 100);

    this.setupMessageListener();
    this.setupStorageListener();
    this.setupBroadcastChannel();
  }

  ngOnDestroy(): void {
    this.cleanupListeners();
  }

  private setupMessageListener(): void {
    this.messageListener = this.handleOAuthMessage.bind(this);
    window.addEventListener('message', this.messageListener);
  }

  private setupStorageListener(): void {
    this.storageListener = this.handleStorageChange.bind(this);
    window.addEventListener('storage', this.storageListener);
  }

  private setupBroadcastChannel(): void {
    if ('BroadcastChannel' in window) {
      try {
        this.broadcastChannel = new BroadcastChannel('oauth_channel');
        this.broadcastChannel.onmessage = (event) => {
          if (
            event.data &&
            event.data.type === 'OAUTH_TOKEN' &&
            event.data.token
          ) {
            this.handleOAuthSuccess(event.data.token);
          }
        };
      } catch (e) {}
    }
  }

  private cleanupListeners(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
    }
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
    if (this.popupCheckInterval) {
      clearInterval(this.popupCheckInterval);
    }
    if (this.localStorageCheckInterval) {
      clearInterval(this.localStorageCheckInterval);
    }
  }

  private handleOAuthMessage(event: MessageEvent): void {
    const allowedOrigin = new URL(environment.websocketUrl).origin;

    if (
      event.origin !== allowedOrigin &&
      event.origin !== window.location.origin
    ) {
      return;
    }

    if (event.data && event.data.type === 'OAUTH_TOKEN' && event.data.token) {
      this.handleOAuthSuccess(event.data.token);
    }
  }

  private handleStorageChange(event: StorageEvent): void {
    if (event.key === 'oauth_token' && event.newValue) {
      const token = event.newValue;
      const timestamp = localStorage.getItem('oauth_timestamp');
      const age = timestamp ? Date.now() - parseInt(timestamp) : null;

      if (timestamp && age !== null && age < 30000) {
        this.handleOAuthSuccess(token);

        localStorage.removeItem('oauth_token');
        localStorage.removeItem('oauth_timestamp');
      }
    }
  }

  private handleOAuthSuccess(token: string): void {
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    try {
      localStorage.removeItem('oauth_communicated');
    } catch (e) {}

    if (this.popupCheckInterval) {
      clearInterval(this.popupCheckInterval);
      this.popupCheckInterval = null;
    }
    if (this.localStorageCheckInterval) {
      clearInterval(this.localStorageCheckInterval);
      this.localStorageCheckInterval = null;
    }

    this.authService.setToken(token);

    this.authService.forceRefreshUser().subscribe({
      next: (user) => {
        if (user?.is_admin) {
          this.router.navigate(['/admin/dashboard'], { replaceUrl: true });
        } else {
          this.router.navigate(['/home'], { replaceUrl: true });
        }
      },
      error: (error) => {
        this.isSubmitting = false;
        this.authService.clearAuthData();
        this.errorMessage = 'Authentication failed. Please try again.';
      },
    });
  }

  openGoogleOAuth(): void {
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    localStorage.removeItem('oauth_token');
    localStorage.removeItem('oauth_timestamp');
    localStorage.removeItem('oauth_communicated');

    const popup = window.open(
      this.googleOAuthUrl,
      'GoogleLogin',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes,noopener=no,noreferrer=no`
    );

    if (!popup) {
      this.errorMessage =
        'Popup blocked. Please allow popups for this site and try again.';
      return;
    }

    this.popupCheckInterval = setInterval(() => {
      if (popup.closed) {
        clearInterval(this.popupCheckInterval);
        this.popupCheckInterval = null;

        setTimeout(() => {
          const token = localStorage.getItem('oauth_token');
          const timestamp = localStorage.getItem('oauth_timestamp');
          const age = timestamp ? Date.now() - parseInt(timestamp) : null;

          if (token && timestamp && age !== null && age < 30000) {
            this.handleOAuthSuccess(token);
            localStorage.removeItem('oauth_token');
            localStorage.removeItem('oauth_timestamp');
          }
        }, 500);
      }
    }, 500);

    this.localStorageCheckInterval = setInterval(() => {
      const token = localStorage.getItem('oauth_token');
      const timestamp = localStorage.getItem('oauth_timestamp');

      if (token && timestamp && Date.now() - parseInt(timestamp) < 30000) {
        this.handleOAuthSuccess(token);
        localStorage.removeItem('oauth_token');
        localStorage.removeItem('oauth_timestamp');

        if (this.localStorageCheckInterval) {
          clearInterval(this.localStorageCheckInterval);
          this.localStorageCheckInterval = null;
        }
      }
    }, 250);

    setTimeout(() => {
      if (this.localStorageCheckInterval) {
        clearInterval(this.localStorageCheckInterval);
        this.localStorageCheckInterval = null;
      }
    }, 60000);
  }

  private updateCaptchaVisibility(): void {
    this.showCaptcha = this.loginAttemptService.shouldShowCaptcha();
    this.remainingAttempts = this.loginAttemptService.getRemainingAttempts();
  }

  onRecaptchaChange(token: string): void {
    this.recaptchaToken = token;
    this.recaptchaErrorMessage = '';
  }

  onRecaptchaError(error: string): void {
    this.recaptchaToken = '';
    this.recaptchaErrorMessage = error;
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  get email() {
    return this.loginForm.get('email');
  }

  get password() {
    return this.loginForm.get('password');
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      Object.keys(this.loginForm.controls).forEach((key) => {
        const control = this.loginForm.get(key);
        control?.markAsTouched();
      });
      return;
    }

    if (this.showCaptcha && !this.recaptchaToken) {
      this.recaptchaErrorMessage = 'Please complete the security verification';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const loginData: any = {
      email: this.email?.value,
      password: this.password?.value,
    };

    if (this.showCaptcha) {
      loginData.recaptchaToken = this.recaptchaToken;
    }

    this.authService.login(loginData).subscribe({
      next: (response) => {
        this.loginAttemptService.resetFailedAttempts();

        const isAdmin = response.user?.is_admin || false;

        if (isAdmin) {
          this.router.navigate(['/admin/dashboard'], { replaceUrl: true });
        } else {
          this.router.navigate(['/home'], { replaceUrl: true });
        }
      },
      error: (error) => {
        this.isSubmitting = false;

        const attemptCount = this.loginAttemptService.incrementFailedAttempts();
        this.updateCaptchaVisibility();

        let errorMsg =
          error.message ||
          'Login failed. Please check your credentials and try again.';

        if (!this.showCaptcha && this.remainingAttempts > 0) {
          errorMsg += ` (${this.remainingAttempts} attempts remaining before security verification required)`;
        } else if (attemptCount === 5) {
          errorMsg +=
            ' Security verification is now required for subsequent login attempts.';
        }

        this.errorMessage = errorMsg;

        if (this.showCaptcha) {
          this.recaptchaToken = '';
        }
      },
    });
  }
}
